import { ProductImageModel } from './../../models/synchronization/productImageModel';
import { ActionType } from './../../common/enums';
import { LocationSyncModel } from '../../models/synchronization/locationSyncModel';
import { ClientSyncModel } from '../../models/synchronization/clientSyncModel';
import { OfferSyncModel } from '../../models/synchronization/offerSyncModel';
import { IProduct, IOffer, IClient, ILocation, IProductImage } from '../../common/appDatabase';
import { ProductModel } from '../../models/product/productModel';
import { AppDatabase, ICategory } from '../../common/appDatabase';
import { fomatDateTimeAsString } from "../../common/functions/helperFunctions";
import SynchronizationModel from "../../models/synchronization/synchronizationModel";
import { CategoryModel } from "../../models/category";
import AuthService from '../authService';

class LocalSynchronizationService {
    db = new AppDatabase();
    authService = new AuthService();

    loadData(data: SynchronizationModel) {
        let result = new Promise((resolve, reject) => {
            this.db.transaction('rw', [this.db.products, this.db.categories, this.db.offers, this.db.clients, this.db.locations, this.db.productImages], async () => {
                await this.processCategories(data);
                await this.processProducts(data);
                await this.processClients(data);
                await this.processLocations(data);
                await this.processOffers(data);
                await this.processProductImages(data);
                this.updateLastSyncDateAndUser(data.lastSyncDate);

            }).then(
                () => {
                    resolve(true)
                }
            ).catch(err => {
                console.log(err);
                reject('Error inserting synchronization data from server');
            })
        });

        return result;
    }

    ensureDbCreated() {
        return new Promise((resolve, reject) => {
            this.db.transaction('r', this.db.offers, async () => {
                await this.db.offers.count();
            }).then(
                () => {
                    resolve(true);
                }
            ).catch(err => {
                console.log('db transaction error');
                reject(err);
            })
        })
    }

    clearDatabase() {
        return new Promise((resolve, reject) => {
            this.db.transaction('rw', this.db.offers, this.db.categories, this.db.clients, this.db.locations, this.db.products, async () => {
                await this.db.categories.clear();
                await this.db.clients.clear();
                await this.db.locations.clear();
                await this.db.offers.clear();
                await this.db.products.clear();

            }).then(
                () => {
                    resolve(true);
                }
            ).catch(err => {
                console.log(err);
                reject('Error clearing local database');
            })
        })
    }

    private processCategories = async (data: SynchronizationModel) => {
        let categoriesInDbCount = await this.db.categories.count();

        if (categoriesInDbCount === 0) {
            // There aren't any categories in local db => insert it all
            let categoriesForDb = new Array<ICategory>();
            data.categories.forEach(category => {
                if (category.isActive) {
                    categoriesForDb.push(this.categoryToDbCategory(category));
                }
            });

            this.db.categories.bulkAdd(categoriesForDb);
        } else {
            for (let i = 0; i < data.categories.length; i++) {
                let remoteCategory = data.categories[i];
                this.db.categories.get({ remoteId: remoteCategory.id }, async (foundCategory) => {
                    if (foundCategory) {
                        // Category is deactivated => delete
                        if (!remoteCategory.isActive) {
                            await this.db.categories.delete(foundCategory.id!)
                        } else {
                            // Category is already in local db => update
                            await this.db.categories.update(parseInt(foundCategory.id!.toString()), {
                                remoteId: remoteCategory.id,
                                parentId: remoteCategory.parentId,
                                name: remoteCategory.name,
                                levelNo: remoteCategory.levelNo,
                                orderNo: remoteCategory.orderNo,
                                isActive: remoteCategory.isActive,
                                categoryTypeId: remoteCategory.categoryTypeId,
                            });
                        }
                    } else {
                        // Category is not in local db => add
                        let dbCategory = this.categoryToDbCategory(remoteCategory);
                        await this.db.categories.add(dbCategory)
                    }
                })
            }

            // Remove deleted categories if exists
            if (data.actionHistory && data.actionHistory.length > 0) {
                let deletedCategories = data.actionHistory.filter(o => o.name === 'Category' && o.actionTypeId === ActionType.Delete);

                if (deletedCategories && deletedCategories.length > 0) {
                    for (let i = 0; i < deletedCategories.length; i++) {
                        let dbCategory = await this.db.categories.get({ remoteId: deletedCategories[i].actionedId });
                        if (dbCategory) {
                            await this.db.categories.delete(dbCategory.id!);
                        }
                    }
                }
            }
        }
    }

    private processProducts = async (data: SynchronizationModel) => {
        let productsInDbCount = await this.db.products.count();

        if (productsInDbCount === 0) {
            // There aren't any products in local db => insert it all
            let productsForDb = new Array<IProduct>();
            data.products.forEach(product => {
                if (product.isActive) {
                    productsForDb.push(this.productToDbProduct(product));
                }
            });

            this.db.products.bulkAdd(productsForDb);
        } else {
            for (let i = 0; i < data.products.length; i++) {
                let remoteProduct = data.products[i];
                this.db.products.get({ remoteId: remoteProduct.productId }, async (foundProduct) => {
                    if (foundProduct) {
                        // Product is deactivated => delete
                        if (!remoteProduct.isActive) {
                            await this.db.products.delete(foundProduct.id!)
                        } else {
                            // Product is already in local db => update
                            foundProduct.remoteId = remoteProduct.productId;
                            foundProduct.categoryId = remoteProduct.categoryId;
                            foundProduct.articleNumber = remoteProduct.articleNumber;
                            foundProduct.brand = remoteProduct.brand;
                            foundProduct.model = remoteProduct.model;
                            foundProduct.supplier = remoteProduct.supplier;
                            foundProduct.power = remoteProduct.power;
                            foundProduct.secondPower = remoteProduct.secondPower;
                            foundProduct.price = remoteProduct.price;
                            foundProduct.priceFromSupplier = remoteProduct.priceFromSupplier;
                            foundProduct.discount = remoteProduct.discount;
                            foundProduct.description = remoteProduct.description;
                            foundProduct.isActive = remoteProduct.isActive;
                            foundProduct.unitId = remoteProduct.unitId;
                            foundProduct.article = remoteProduct.article;
                            foundProduct.fileId = remoteProduct.fileId;
                            foundProduct.diameter = remoteProduct.diameter;
                            foundProduct.relatedProducts = remoteProduct.relatedProducts;

                            await this.db.products.update(parseInt(foundProduct.id!.toString()), foundProduct);
                        }
                    } else {
                        // Product is not in local db => add
                        let dbProduct = this.productToDbProduct(remoteProduct);
                        await this.db.products.add(dbProduct);
                    }
                })
            }

            // Remove deleted products if exists
            if (data.actionHistory && data.actionHistory.length > 0) {
                let deletedProducts = data.actionHistory.filter(o => o.name === 'Product' && o.actionTypeId === ActionType.Delete);

                if (deletedProducts && deletedProducts.length > 0) {
                    for (let i = 0; i < deletedProducts.length; i++) {
                        let dbProduct = await this.db.products.get({ remoteId: deletedProducts[i].actionedId });
                        if (dbProduct) {
                            await this.db.products.delete(dbProduct.id!);
                        }
                    }
                }
            }
        }
    }

    private processOffers = async (data: SynchronizationModel) => {
        let offersInDbCount = await this.db.offers.count();

        if (offersInDbCount === 0) {
            // There aren't any offers in local db => insert it all
            let offersForDb = new Array<IOffer>();
            data.offers.forEach(async (offer, index) => {
                let dbOffer = this.offerToDbOffer(offer);

                let localClient = await this.db.clients.get({ remoteId: offer.clientId });
                let localLocation = await this.db.locations.get({ remoteId: offer.locationId });

                dbOffer.clientId = localClient?.id!;
                dbOffer.locationId = localLocation?.id!;

                offersForDb.push(dbOffer);

                if (index + 1 === data.offers.length) {
                    await this.db.offers.bulkAdd(offersForDb);
                }
            });
        } else {
            // Remove offers that has been transfered to another trader
            if (this.authService.checkIsTrader()) {
                let userId = this.authService.getUserId();
                let recordsToCheck = data.actionHistory.filter(o => o.name === "Offer" && o.actionTypeId === ActionType.OfferTraderChange && o.actionedId === userId);

                if (recordsToCheck && recordsToCheck.length > 0) {
                    for (let i = 0; i < recordsToCheck.length; i++) {
                        let dbOffer = await this.db.offers.get({ remoteId: recordsToCheck[i].offerId });
                        if (dbOffer) {
                            await this.db.offers.delete(dbOffer.id!)
                        }
                    }
                }
            }

            for (let i = 0; i < data.offers.length; i++) {
                let remoteOffer = data.offers[i];
                this.db.offers.get({ remoteId: remoteOffer.id }, async (foundOffer) => {
                    if (foundOffer) {
                        // Offer is already in local db => update
                        let localClient = await this.db.clients.get({ remoteId: remoteOffer.clientId });
                        let localLocation = await this.db.locations.get({ remoteId: remoteOffer.locationId });

                        foundOffer.clientId = localClient?.id!;
                        foundOffer.locationId = localLocation?.id!;

                        foundOffer.client = remoteOffer.client;
                        foundOffer.clientEmail = remoteOffer.clientEmail;
                        foundOffer.clientName = remoteOffer.clientName;
                        foundOffer.clientPhone = remoteOffer.clientPhone;
                        foundOffer.date = remoteOffer.date;
                        foundOffer.discount = remoteOffer.discount;
                        foundOffer.fixedDiscount = remoteOffer.fixedDiscount;
                        foundOffer.isActive = remoteOffer.isActive;
                        foundOffer.location = remoteOffer.location;
                        foundOffer.locationAddress = remoteOffer.locationAddress;
                        foundOffer.locationDistrict = remoteOffer.locationDistrict;
                        foundOffer.locationMunicipality = remoteOffer.locationMunicipality;
                        foundOffer.locationTown = remoteOffer.locationTown;
                        foundOffer.notes = remoteOffer.notes;
                        foundOffer.adminNotes = remoteOffer.adminNotes;
                        foundOffer.number = remoteOffer.number;
                        foundOffer.price = remoteOffer.price;
                        foundOffer.statusId = remoteOffer.statusId;
                        foundOffer.trader = remoteOffer.trader;
                        foundOffer.traderId = remoteOffer.traderId;
                        foundOffer.isolationTypeId = remoteOffer.isolationTypeId;
                        foundOffer.roomHeight = remoteOffer.roomHeight;
                        foundOffer.planner = remoteOffer.planner;
                        foundOffer.activeStep = remoteOffer.activeStep;
                        foundOffer.completedSteps = remoteOffer.completedSteps;
                        foundOffer.gasInstallation = remoteOffer.gasInstallation;
                        foundOffer.heathInstallation = remoteOffer.heathInstallation;
                        foundOffer.disclaimer = remoteOffer.disclaimer;
                        foundOffer.isWithHeathInstall = remoteOffer.isWithHeathInstall;
                        foundOffer.isWithWorkAndMaterials = remoteOffer.isWithWorkAndMaterials;
                        foundOffer.reported = foundOffer.reported === 0 ? 0 : 1;

                        if (remoteOffer.files) {
                            remoteOffer.files.forEach(file => {
                                file.updatedOn = this.getDate(file.updatedOn);
                            })
                        }

                        foundOffer.files = remoteOffer.files;
                        foundOffer.updatedOn = this.getDate(remoteOffer.updatedOn);
                        foundOffer.priceChanged = remoteOffer.priceChanged;
                        foundOffer.remoteUpdatedOn = this.getDate(remoteOffer.remoteUpdatedOn);
                        foundOffer.statusHistory = remoteOffer.statusHistory;
                        foundOffer.equipmentOnly = remoteOffer.equipmentOnly;

                        await this.db.offers.update(parseInt(foundOffer.id!.toString()), foundOffer);
                    } else {
                        // Offer is not in local db => add
                        let dbOffer = this.offerToDbOffer(remoteOffer);

                        let localClient = await this.db.clients.get({ remoteId: remoteOffer.clientId });
                        let localLocation = await this.db.locations.get({ remoteId: remoteOffer.locationId });

                        dbOffer.clientId = localClient?.id!;
                        dbOffer.locationId = localLocation?.id!;

                        await this.db.offers.add(dbOffer);
                    }
                })
            }
        }

        var offersInDb = await this.db.offers.toArray();

        if (data.offers && data.offers.length > 0 && offersInDb && offersInDb.length > 0) {
            let offersToDelete = Array<number>();

            for (let i = 0; i < offersInDb.length; i++) {
                if (!data.offers.find(o => o.id === offersInDb[i].remoteId)) {
                    if (offersInDb[i].remoteId === 0 && offersInDb[i].reported === 1) {
                        offersToDelete.push(offersInDb[i].id!);
                    }
                }
            }

            if (offersToDelete.length > 0) {
                this.db.offers.bulkDelete(offersToDelete);
            }
        }

        // Remove deleted offers if exists
        if (data.actionHistory && data.actionHistory.length > 0) {
            let deletedOffers = data.actionHistory.filter(o => o.name === 'Offer' && o.actionTypeId === ActionType.Delete);

            if (deletedOffers && deletedOffers.length > 0) {
                for (let i = 0; i < deletedOffers.length; i++) {
                    let dbOffer = await this.db.offers.get({ remoteId: deletedOffers[i].actionedId });
                    if (dbOffer) {
                        await this.db.offers.delete(dbOffer.id!);
                    }
                }
            }
        }

        localStorage.setItem('offersCount', data.offersCount.toString());
    }

    private processClients = async (data: SynchronizationModel) => {
        let clientsInDbCount = await this.db.clients.count();

        if (clientsInDbCount === 0) {
            // There aren't any clients in local db => insert it all
            let clientsForDb = new Array<IClient>();
            data.clients.forEach(client => {
                clientsForDb.push(this.clientToDbClient(client));
            });

            this.db.clients.bulkAdd(clientsForDb);
        } else {
            for (let i = 0; i < data.clients.length; i++) {
                let remoteClient = data.clients[i];
                this.db.clients.get({ remoteId: remoteClient.id }, async (foundClient) => {
                    if (foundClient) {
                        // Client is already in local db => update
                        await this.db.clients.update(parseInt(foundClient.id!.toString()), {
                            remoteId: remoteClient.id,
                            email: remoteClient.email,
                            name: remoteClient.name,
                            phone: remoteClient.phone,
                        })
                    } else {
                        // Client is not in local db => add
                        let dbClient = this.clientToDbClient(remoteClient);
                        await this.db.clients.add(dbClient);
                    }
                })
            }

            var clientsInDb = await this.db.clients.toArray();

            if (data.clients && data.clients.length > 0 && clientsInDb && clientsInDb.length > 0) {
                let clientsToDelete = Array<number>();

                for (let i = 0; i < clientsInDb.length; i++) {
                    if (!data.clients.find(o => o.id === clientsInDb[i].remoteId)) {
                        if (clientsInDb[i].remoteId === 0 && clientsInDb[i].reported === 1) {
                            clientsToDelete.push(clientsInDb[i].id!);
                        }
                    }
                }

                if (clientsToDelete.length > 0) {
                    this.db.clients.bulkDelete(clientsToDelete);
                }
            }
        }
    }

    private processLocations = async (data: SynchronizationModel) => {
        let locationsInDbCount = await this.db.locations.count();
        let dbClients = await this.db.clients.toArray();

        if (locationsInDbCount === 0) {
            // There aren't any locations in local db => insert it all
            let locationsForDb = new Array<ILocation>();
            data.locations.forEach(location => {
                let dbLocation = this.locationToDbLocation(location);

                if (dbClients) {
                    let client = dbClients.find(c => c.remoteId === location.clientId);
                    if (client) {
                        // work only with local id's
                        dbLocation.clientId = client.id!
                    }
                }

                locationsForDb.push(dbLocation);
            });

            this.db.locations.bulkAdd(locationsForDb);
        } else {
            for (let i = 0; i < data.locations.length; i++) {
                let remoteLocation = data.locations[i];
                this.db.locations.get({ remoteId: remoteLocation.id }, async (foundLocation) => {
                    if (foundLocation) {
                        // Location is already in local db => update
                        foundLocation.remoteId = remoteLocation.id;
                        foundLocation.address = remoteLocation.address;
                        foundLocation.district = remoteLocation.district;
                        foundLocation.municipality = remoteLocation.municipality;
                        foundLocation.town = remoteLocation.town;
                        foundLocation.latitude = remoteLocation.latitude;
                        foundLocation.longitude = remoteLocation.longitude;

                        if (dbClients) {
                            let client = dbClients.find(c => c.remoteId === remoteLocation.clientId);
                            if (client) {
                                // work only with local id's
                                foundLocation.clientId = client.id!
                            }
                        }

                        await this.db.locations.update(parseInt(foundLocation.id!.toString()), foundLocation);
                    } else {
                        // Location is not in local db => add
                        let dbLocation = this.locationToDbLocation(remoteLocation);

                        if (dbClients) {
                            let client = dbClients.find(c => c.remoteId === remoteLocation.clientId);
                            if (client) {
                                // work only with local id's
                                dbLocation.clientId = client.id!
                            }
                        }

                        await this.db.locations.add(dbLocation);
                    }
                })
            }

            var locationsInDb = await this.db.locations.toArray();

            if (data.locations && data.locations.length > 0 && locationsInDb && locationsInDb.length > 0) {
                let locationsToDelete = Array<number>();

                for (let i = 0; i < locationsInDb.length; i++) {
                    if (!data.locations.find(o => o.id === locationsInDb[i].remoteId)) {
                        if (locationsInDb[i].remoteId === 0 && locationsInDb[i].reported === 1) {
                            locationsToDelete.push(locationsInDb[i].id!);
                        }
                    }
                }

                if (locationsToDelete.length > 0) {
                    this.db.locations.bulkDelete(locationsToDelete);
                }
            }
        }
    }

    private processProductImages = async (data: SynchronizationModel) => {
        let productImagesInDbCount = await this.db.productImages.count();

        await this.db.productImages.clear();

        if (data.productImages && data.productImages.length > 0) {
            if (productImagesInDbCount === 0) {
                let productImagesForDb = new Array<IProductImage>();
                data.productImages.forEach(productImage => {
                    productImagesForDb.push(this.productImagetoDbProductImage(productImage));
                });

                this.db.productImages.bulkAdd(productImagesForDb);
            } else {
                for (let i = 0; i < data.productImages.length; i++) {
                    let remoteProductImage = data.productImages[i];
                    this.db.productImages.get({ remoteId: remoteProductImage.fileId }, async (foundProductImage) => {
                        if (foundProductImage) {
                            await this.db.productImages.update(parseInt(foundProductImage.id!.toString()), {
                                remoteId: remoteProductImage.fileId,
                                name: remoteProductImage.name,
                                contentType: remoteProductImage.contentType,
                                content: remoteProductImage.content,
                            });
                        } else {
                            // Product is not in local db => add
                            let dbProductImage = this.productImagetoDbProductImage(remoteProductImage);
                            await this.db.productImages.add(dbProductImage);
                        }
                    })
                }
            }
        }
    }

    private categoryToDbCategory(category: CategoryModel): ICategory {
        return {
            remoteId: category.id,
            isActive: category.isActive,
            levelNo: category.levelNo,
            name: category.name,
            orderNo: category.orderNo,
            parentId: category.parentId,
            categoryTypeId: category.categoryTypeId,
        }
    }

    private productToDbProduct(product: ProductModel): IProduct {
        return {
            remoteId: product.productId,
            articleNumber: product.articleNumber,
            brand: product.brand,
            model: product.model,
            categoryId: product.categoryId,
            description: product.description,
            discount: product.discount,
            isActive: product.isActive,
            power: product.power,
            secondPower: product.secondPower,
            price: product.price,
            priceFromSupplier: product.priceFromSupplier,
            supplier: product.supplier,
            article: product.article,
            relatedProducts: product.relatedProducts,
            unitId: product.unitId,
            category: product.category,
            fileId: product.fileId,
            diameter: product.diameter,
        }
    }

    private offerToDbOffer(offer: OfferSyncModel): IOffer {
        if (offer.files) {
            offer.files.forEach(file => {
                file.updatedOn = this.getDate(file.updatedOn);
            })
        }

        return {
            remoteId: offer.id,
            client: offer.client,
            clientEmail: offer.clientEmail,
            clientId: offer.clientId,
            clientName: offer.clientName,
            clientPhone: offer.clientPhone,
            date: offer.date,
            discount: offer.discount,
            fixedDiscount: offer.fixedDiscount,
            isActive: offer.isActive,
            location: offer.location,
            locationAddress: offer.locationAddress,
            locationDistrict: offer.locationDistrict,
            locationId: offer.locationId,
            locationMunicipality: offer.locationMunicipality,
            locationTown: offer.locationTown,
            locationLatitude: offer.locationLatitude,
            locationLongitude: offer.locationLongitude,
            notes: offer.notes,
            adminNotes: offer.notes,
            number: offer.number,
            price: offer.price,
            statusId: offer.statusId,
            trader: offer.trader,
            traderId: offer.traderId,
            isolationTypeId: offer.isolationTypeId,
            activeStep: offer.activeStep,
            completedSteps: offer.completedSteps,
            disclaimer: offer.disclaimer,
            isWithHeathInstall: offer.isWithHeathInstall,
            isWithWorkAndMaterials: offer.isWithWorkAndMaterials,
            planner: offer.planner,
            roomHeight: offer.roomHeight,
            gasInstallation: offer.gasInstallation,
            heathInstallation: offer.heathInstallation,
            reported: 1,
            files: offer.files,
            updatedOn: this.getDate(offer.updatedOn),
            priceChanged: offer.priceChanged,
            remoteUpdatedOn: this.getDate(offer.remoteUpdatedOn),
            statusHistory: offer.statusHistory,
            equipmentOnly: offer.equipmentOnly,
        }
    }

    private clientToDbClient = (client: ClientSyncModel): IClient => {
        return {
            remoteId: client.id,
            email: client.email,
            name: client.name,
            phone: client.phone,
            reported: 1
        }
    }

    private locationToDbLocation = (location: LocationSyncModel): ILocation => {
        return {
            remoteId: location.id,
            address: location.address,
            clientId: location.clientId,
            district: location.district,
            municipality: location.municipality,
            town: location.town,
            reported: 1,
            latitude: location.latitude,
            longitude: location.longitude,
        }
    }

    private productImagetoDbProductImage = (productImage: ProductImageModel): IProductImage => {
        return {
            remoteId: productImage.fileId,
            name: productImage.name,
            contentType: productImage.contentType,
            content: productImage.content,
        }
    }

    private getDate(value: string): string {
        if (value) {
            let dateStr = '';

            try {
                let date = new Date(value);
                dateStr = fomatDateTimeAsString(date);
            } catch (error) {
                console.log(error);
            }

            return dateStr;
        }

        return ''
    }

    private updateLastSyncDateAndUser(value: string) {
        let date = new Date(value);
        let dateStr = fomatDateTimeAsString(date);
        localStorage.setItem('lastSyncDate', dateStr);
        localStorage.setItem('userId', this.authService.getUserId());
    }
}

export default LocalSynchronizationService;